/**
 * P13-H1 fix — JSON Schema → Zod raw-shape converter.
 *
 * The MCP TypeScript SDK at 1.29 requires `inputSchema`/`outputSchema` on
 * `registerTool` to be a Zod *raw shape* (object literal whose values are Zod
 * schemas) OR a Zod object schema. Our Phase-1 contract carries tool schemas as
 * JSON Schema (so we can round-trip them deterministically). The Phase-2 emit
 * used `as JsonSchemaShape` to silence the type error, which the Phase-13 harness
 * proved fails at runtime ("inputSchema must be a Zod schema or raw shape,
 * received an unrecognized object").
 *
 * This converter walks a JSON Schema *object* (`{type:"object", properties: ...,
 * required: [...]}`) and emits a TypeScript SOURCE STRING that constructs the
 * matching Zod raw shape. The source is what the generator inlines into the
 * emitted server's `src/server.ts`.
 *
 * Scope:
 *  - `type: "object"` at the root (the SDK requires this).
 *  - Property types: string, number, integer, boolean, array, object, null,
 *    array of types (incl. nullable: `["string","null"]`).
 *  - String formats we map: `email`, `date`, `date-time`, `url`, `uuid`.
 *  - String constraints: `enum`, `minLength`, `maxLength`, `pattern`.
 *  - Number constraints: `minimum`, `maximum`, `exclusiveMinimum`, `exclusiveMaximum`.
 *  - Array constraints: `items`, `minItems`, `maxItems`.
 *  - Required vs optional via `required: [...]`.
 *  - `anyOf` → `z.union([...])`. `oneOf`/`allOf` already collapsed to `anyOf`
 *    by `schema-conversion.ts`, so we only see `anyOf` here.
 *  - Unknown / missing type → `z.unknown()` (preserves opacity, never throws).
 *
 * Out of scope (degrades gracefully to z.unknown):
 *  - Schema composition beyond `anyOf` (we already pre-flattened).
 *  - References ($ref) — the intake stage resolves them.
 *  - Custom keywords beyond the v0 set above.
 *
 * Determinism: the same JSON Schema produces the same source string byte-for-byte.
 * We sort property keys lexicographically so the emit is order-independent.
 */

export interface JsonSchemaToZodOptions {
  /** When true, the root output is `z.object({...})`; when false, the raw shape `{...}`. Default: false (raw shape, the SDK's registerTool contract). */
  asObject?: boolean;
}

/**
 * Render a JSON-Schema-shaped value as a TypeScript expression that constructs
 * the matching Zod schema. The returned string is what gets inlined.
 */
export function jsonSchemaToZodSource(
  schema: unknown,
  opts: JsonSchemaToZodOptions = {}
): string {
  if (!isObj(schema)) return "z.unknown()";

  // Object root → raw shape (the SDK's preferred form for registerTool).
  if (schema.type === "object" || (isObj(schema.properties) && !schema.type)) {
    const props = (schema.properties ?? {}) as Record<string, unknown>;
    const required = Array.isArray(schema.required) ? new Set(schema.required as string[]) : new Set<string>();
    const keys = Object.keys(props).sort();
    const lines = keys.map((k) => {
      const propSrc = renderValue(props[k]);
      const wrapped = required.has(k) ? propSrc : `${propSrc}.optional()`;
      return `  ${JSON.stringify(k)}: ${wrapped}`;
    });
    const shapeBody = `{\n${lines.join(",\n")}\n}`;
    return opts.asObject ? `z.object(${shapeBody})` : shapeBody;
  }

  return renderValue(schema);
}

function renderValue(node: unknown): string {
  if (!isObj(node)) return "z.unknown()";

  // anyOf precedence (schema-conversion.ts already collapsed oneOf/allOf into anyOf).
  if (Array.isArray(node.anyOf)) {
    const parts = node.anyOf.map((s) => renderValue(s));
    if (parts.length === 1) return parts[0];
    return `z.union([${parts.join(", ")}])`;
  }

  // Type can be a string, an array (e.g. ["string", "null"]), or absent.
  const t = node.type;
  if (Array.isArray(t)) {
    // Nullable shortcut: ["X","null"] → renderValue({...,type:"X"}).nullable()
    if (t.length === 2 && t.includes("null")) {
      const real = t.find((x) => x !== "null") as string;
      const inner = renderValue({ ...node, type: real });
      return `${inner}.nullable()`;
    }
    // General union of types — render each as its own typed schema.
    const parts = t.map((tt) => renderValue({ ...node, type: tt }));
    return `z.union([${parts.join(", ")}])`;
  }

  switch (t) {
    case "string":
      return renderString(node);
    case "integer":
      return renderNumber(node, true);
    case "number":
      return renderNumber(node, false);
    case "boolean":
      return "z.boolean()";
    case "array":
      return renderArray(node);
    case "object":
      return jsonSchemaToZodSource(node, { asObject: true });
    case "null":
      return "z.null()";
    default:
      return "z.unknown()";
  }
}

function renderString(node: Record<string, unknown>): string {
  const en = node.enum;
  if (Array.isArray(en) && en.every((v) => typeof v === "string")) {
    const literals = (en as string[]).map((v) => JSON.stringify(v)).join(", ");
    if (en.length === 1) return `z.literal(${literals})`;
    // No `as const` — that's TS-only syntax. Runtime z.enum accepts an array;
    // the emitted code will lose const-narrowing but Zod validation still works.
    return `z.enum([${literals}])`;
  }
  let out = "z.string()";
  const fmt = node.format;
  if (fmt === "email") out += ".email()";
  else if (fmt === "uri" || fmt === "url") out += ".url()";
  else if (fmt === "uuid") out += ".uuid()";
  else if (fmt === "date-time") out += ".datetime({ offset: true })";
  else if (fmt === "date") out += ".regex(/^\\d{4}-\\d{2}-\\d{2}$/)";
  if (typeof node.minLength === "number") out += `.min(${node.minLength})`;
  if (typeof node.maxLength === "number") out += `.max(${node.maxLength})`;
  if (typeof node.pattern === "string") out += `.regex(new RegExp(${JSON.stringify(node.pattern)}))`;
  return out;
}

function renderNumber(node: Record<string, unknown>, integer: boolean): string {
  let out = integer ? "z.number().int()" : "z.number()";
  if (typeof node.minimum === "number") out += `.min(${node.minimum})`;
  if (typeof node.maximum === "number") out += `.max(${node.maximum})`;
  if (typeof node.exclusiveMinimum === "number") out += `.gt(${node.exclusiveMinimum})`;
  if (typeof node.exclusiveMaximum === "number") out += `.lt(${node.exclusiveMaximum})`;
  return out;
}

function renderArray(node: Record<string, unknown>): string {
  const items = node.items;
  const inner = isObj(items) || Array.isArray(items) ? renderValue(items) : "z.unknown()";
  let out = `z.array(${inner})`;
  if (typeof node.minItems === "number") out += `.min(${node.minItems})`;
  if (typeof node.maxItems === "number") out += `.max(${node.maxItems})`;
  return out;
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
